import type { Data, Layout } from "plotly.js";
import { METER_TRACE_COLORS, NL_REGIONS, REGIONAL_METER_TARGET, ECG_MAINS_RIBBON_COLOR, ECG_REGION_RIBBON_COLOR, ribbonLineOffsetHz, type RegionalRangeMinutes } from "../constants/regions";
import type { RegionalMeterPoint } from "../types/regional";
import { regionalRangeMinutesToKey, relativeTimeTicks, toRelativeSeconds } from "./relativeTimeAxis";
import { buildDateTickFormatStops } from "./powerChartAxis";

const METER_OK_COLOR = "#006fff";
const METER_WARN_COLOR = "#f59e0b";
const METER_ALERT_COLOR = "#dc2626";

const DEFAULT_EMA_TAU_MS = 30_000;
const MIN_Y_SPAN_HZ = 0.02;

/** @deprecated Use all pre-window samples for EMA; kept for callers that still reference it. */
export const EMA_WARMUP_MAX_MS = 30_000;

/** @deprecated EMA now uses full history before the display window. */
export function emaWarmupLookbackMs(emaTauMs: number | null): number {
  if (emaTauMs == null || emaTauMs <= 0) return 0;
  return emaTauMs;
}

/** Matches freq-service regional arbitration max_age_ms. */
export const REGIONAL_METER_STALE_MS = 2000;

/** Map: region is dead if no sample in this window (display only). */
export const MAP_FREQ_SAMPLE_MAX_AGE_MS = 10_000;

/** Map: keep last good Hz while meters are still online (bridges MQTT gaps). */
export const MAP_FREQ_HOLD_MS = 10_000;

function formatOnlineTitleSuffix(label: string | null): string {
  if (!label) return "";
  return ` <span style="font-weight:500;color:${METER_OK_COLOR}">· ${label}</span>`;
}

/** Show actual count up to target; surplus as `3+1`, `3+2`, … */
export function formatMeterCountLabel(count: number, target = REGIONAL_METER_TARGET): string {
  if (count <= target) return String(count);
  return `${target}+${count - target}`;
}

/** Title suffix for regional charts — green at target/surplus, orange below, red at zero. */
export function formatRegionalMeterCountSuffix(
  count: number,
  target = REGIONAL_METER_TARGET,
): string {
  const label = formatMeterCountLabel(count, target);
  const base = `font-weight:500`;
  if (count >= target) {
    return ` <span style="${base};color:${METER_OK_COLOR}">· meters: ${label}</span>`;
  }
  if (count === 0) {
    return ` <span style="${base};color:${METER_ALERT_COLOR}">· meters: ${label}</span>`;
  }
  return ` <span style="${base};color:${METER_WARN_COLOR}">· meters: ${label}</span>`;
}

export function countRegionalMetersOnline(
  meters: Map<string, RegionalMeterPoint[]>,
  nowMs: number,
  maxAgeMs = REGIONAL_METER_STALE_MS,
): number {
  let online = 0;
  for (const samples of meters.values()) {
    const last = samples[samples.length - 1];
    if (last && nowMs - last.ts_ms <= maxAgeMs) online += 1;
  }
  return online;
}

export function trimMeterSamples(
  samples: RegionalMeterPoint[],
  cutoffMs: number,
): RegionalMeterPoint[] {
  let start = 0;
  for (let i = samples.length - 1; i >= 0; i -= 1) {
    if (samples[i]!.ts_ms < cutoffMs) {
      start = i + 1;
      break;
    }
  }
  return start > 0 ? samples.slice(start) : samples;
}

export function appendMeterSample(
  samples: RegionalMeterPoint[],
  point: RegionalMeterPoint,
): RegionalMeterPoint[] {
  const last = samples[samples.length - 1];
  if (last && point.ts_ms === last.ts_ms) {
    if (last.freq_hz === point.freq_hz) return samples;
    return [...samples.slice(0, -1), point];
  }
  if (last && point.ts_ms < last.ts_ms) {
    const next = [...samples, point];
    next.sort((a, b) => a.ts_ms - b.ts_ms);
    return next;
  }
  return [...samples, point];
}

function sampleAtOrBefore(
  samples: RegionalMeterPoint[],
  t: number,
): RegionalMeterPoint | null {
  if (!samples.length || t < samples[0]!.ts_ms) return null;

  let lo = 0;
  let hi = samples.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid]!.ts_ms <= t) {
      best = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return best >= 0 ? samples[best]! : null;
}

/** Zero-order hold — null if the held sample is older than maxAgeMs relative to t. */
export function valueAtTime(
  samples: RegionalMeterPoint[],
  t: number,
  maxAgeMs = REGIONAL_METER_STALE_MS,
): number | null {
  const sample = sampleAtOrBefore(samples, t);
  if (!sample || t - sample.ts_ms > maxAgeMs) return null;
  return sample.freq_hz;
}

/** Mean of fresh regional meter readings at time t (matches online meter staleness). */
export function regionalAverageAt(
  meters: Map<string, RegionalMeterPoint[]>,
  t: number,
  maxAgeMs = REGIONAL_METER_STALE_MS,
): number | null {
  let sum = 0;
  let count = 0;
  for (const samples of meters.values()) {
    const v = valueAtTime(samples, t, maxAgeMs);
    if (v != null) {
      sum += v;
      count += 1;
    }
  }
  return count > 0 ? sum / count : null;
}

/**
 * Map readout — average at the newest regional sample, not wall-clock now.
 * Avoids brief "—" when the 1 Hz clock tick runs ahead of the next MQTT sample.
 */
export function regionalAverageForMap(
  meters: Map<string, RegionalMeterPoint[]>,
  nowMs: number,
  maxAgeMs = REGIONAL_METER_STALE_MS,
): number | null {
  let newestTs = 0;
  for (const samples of meters.values()) {
    const last = samples[samples.length - 1];
    if (last && last.ts_ms > newestTs) newestTs = last.ts_ms;
  }
  if (newestTs <= 0 || nowMs - newestTs > MAP_FREQ_SAMPLE_MAX_AGE_MS) return null;
  return regionalAverageAt(meters, newestTs, maxAgeMs);
}

type MapFreqHoldEntry = { hz: number; atMs: number };

/** Avoid map "—" flicker between MQTT samples; drop hold when region goes offline. */
export function mapFreqWithHold(
  regionId: string,
  computed: number | null,
  nowMs: number,
  instantOnline: number,
  cache: Map<string, MapFreqHoldEntry>,
): number | null {
  if (computed != null && Number.isFinite(computed)) {
    cache.set(regionId, { hz: computed, atMs: nowMs });
    return computed;
  }
  const held = cache.get(regionId);
  if (held && instantOnline > 0 && nowMs - held.atMs <= MAP_FREQ_HOLD_MS) {
    return held.hz;
  }
  if (instantOnline === 0) cache.delete(regionId);
  return null;
}

export function emaSmooth(
  points: { t: number; v: number }[],
  tauMs = DEFAULT_EMA_TAU_MS,
  /** Output starts here; earlier points still seed the filter state. */
  displayFromMs?: number,
): { t: number; v: number }[] {
  if (!points.length) return [];
  let ema = points[0]!.v;
  const out: { t: number; v: number }[] = [{ t: points[0]!.t, v: ema }];
  for (let i = 1; i < points.length; i += 1) {
    const prevT = points[i - 1]!.t;
    const cur = points[i]!;
    const dt = Math.max(1, cur.t - prevT);
    const alpha = 1 - Math.exp(-dt / tauMs);
    ema = ema + alpha * (cur.v - ema);
    out.push({ t: cur.t, v: ema });
  }
  if (displayFromMs == null) return out;
  return out.filter((p) => p.t >= displayFromMs);
}

export function buildRegionalAverage(
  meters: Map<string, RegionalMeterPoint[]>,
  emaTauMs: number | null,
  /** When set, EMA may use earlier samples; output starts here. */
  displayFromMs?: number,
  maxAgeMs = REGIONAL_METER_STALE_MS,
): { x: Date[]; y: number[] } {
  const eventTimes = new Set<number>();
  for (const samples of meters.values()) {
    for (const p of samples) eventTimes.add(p.ts_ms);
  }
  const times = [...eventTimes].sort((a, b) => a - b);
  const raw: { t: number; v: number }[] = [];

  for (const t of times) {
    const v = regionalAverageAt(meters, t, maxAgeMs);
    if (v != null) raw.push({ t, v });
  }

  let series =
    emaTauMs != null && emaTauMs > 0
      ? emaSmooth(raw, emaTauMs, displayFromMs)
      : displayFromMs != null
        ? raw.filter((p) => p.t >= displayFromMs)
        : raw;
  return {
    x: series.map((p) => new Date(p.t)),
    y: series.map((p) => p.v),
  };
}

/** When history is shorter than the window, anchor x-axis at first sample (left). */
export function regionalChartXWindow(
  meters: Map<string, RegionalMeterPoint[]>,
  windowMs: number,
  nowMs: number,
): { xMin: number; xMax: number } {
  const cutoff = nowMs - windowMs;
  let earliest = Infinity;
  for (const samples of meters.values()) {
    if (samples.length && samples[0]!.ts_ms < earliest) {
      earliest = samples[0]!.ts_ms;
    }
  }
  if (!Number.isFinite(earliest)) {
    return { xMin: cutoff, xMax: nowMs };
  }
  if (earliest > cutoff) {
    return { xMin: earliest, xMax: earliest + windowMs };
  }
  return { xMin: cutoff, xMax: nowMs };
}

function yRangeForValues(values: number[]): [number, number] | undefined {
  const finite = values.filter((v) => Number.isFinite(v));
  if (!finite.length) return [49.99, 50.01];
  let min = Math.min(...finite);
  let max = Math.max(...finite);
  const mid = (min + max) / 2;
  if (max - min < MIN_Y_SPAN_HZ) {
    min = mid - MIN_Y_SPAN_HZ / 2;
    max = mid + MIN_Y_SPAN_HZ / 2;
  } else {
    const pad = (max - min) * 0.08;
    min -= pad;
    max += pad;
  }
  return [min, max];
}

function seriesXWindow(
  samples: RegionalMeterPoint[],
  windowMs: number,
  nowMs: number,
): { xMin: number; xMax: number } {
  const meters = new Map<string, RegionalMeterPoint[]>();
  if (samples.length) meters.set("series", samples);
  return regionalChartXWindow(meters, windowMs, nowMs);
}

export function buildMainFreqChartPayload({
  chartId,
  chartLabel,
  samples,
  windowMs,
  emaTauMs,
  nowMs,
  meterOnlineLabel = null,
}: {
  chartId: string;
  chartLabel: string;
  samples: RegionalMeterPoint[];
  windowMs: number;
  emaTauMs: number | null;
  nowMs: number;
  /** e.g. "2+1" when main_A, main_B and backup main_C are live. */
  meterOnlineLabel?: string | null;
}): {
  data: Data[];
  layout: Partial<Layout>;
  revision: string;
  readoutHz: number | null;
} {
  const { xMin, xMax } = seriesXWindow(samples, windowMs, nowMs);
  const xRange: [Date, Date] = [new Date(xMin), new Date(xMax)];
  const visible = trimMeterSamples(samples, xMin).filter((p) => p.ts_ms <= xMax);
  const traces: Data[] = [];
  const yValues: number[] = [];
  const smoothing = emaTauMs != null && emaTauMs > 0;

  if (visible.length && smoothing) {
    yValues.push(...visible.map((p) => p.freq_hz));
    traces.push({
      name: "raw",
      x: visible.map((p) => new Date(p.ts_ms)),
      y: visible.map((p) => p.freq_hz),
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", width: 0.5, color: "#94a3b8" },
      opacity: 0.75,
      showlegend: false,
      hovertemplate: `raw<br>%{x|%H:%M:%S.%L}<br>%{y:.4f} Hz<extra></extra>`,
    });
  }

  const forEma = samples.filter((p) => p.ts_ms <= xMax);
  const raw = forEma.map((p) => ({ t: p.ts_ms, v: p.freq_hz }));
  const display =
    smoothing && raw.length ? emaSmooth(raw, emaTauMs!, xMin) : raw.filter((p) => p.t >= xMin);

  if (display.length) {
    yValues.push(...display.map((p) => p.v));
    traces.push({
      name: smoothing ? "avg" : "raw",
      x: display.map((p) => new Date(p.t)),
      y: display.map((p) => p.v),
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", width: 1.5, color: "#006fff" },
      showlegend: false,
      hovertemplate: `${smoothing ? "avg" : "raw"}<br>%{x|%H:%M:%S.%L}<br>%{y:.4f} Hz<extra></extra>`,
    });
  }

  const readoutHz = display.length ? display[display.length - 1]!.v : null;
  const yRange = yRangeForValues(yValues);

  const layout: Partial<Layout> = {
    title: {
      text: `<b>${chartId}</b> <span style="font-weight:400;color:#6c757d">${chartLabel}</span>${formatOnlineTitleSuffix(meterOnlineLabel)}`,
      font: { size: 11, color: "#1a1c1e" },
      x: 0,
      xanchor: "left",
    },
    paper_bgcolor: "#ffffff",
    plot_bgcolor: "#ffffff",
    margin: { l: 44, r: 80, t: 28, b: 28 },
    font: { family: "Inter, sans-serif", size: 10, color: "#6c757d" },
    xaxis: {
      type: "date",
      range: xRange,
      gridcolor: "#e9ecef",
      linecolor: "#e9ecef",
      tickfont: { size: 9, color: "#6c757d" },
      ...buildDateTickFormatStops(windowMs),
    },
    yaxis: {
      title: { text: "Hz", font: { size: 10, color: "#6c757d" } },
      range: yRange,
      gridcolor: "#e9ecef",
      linecolor: "#e9ecef",
      tickfont: { size: 9, color: "#6c757d" },
      tickformat: ".3f",
    },
    showlegend: false,
    annotations:
      readoutHz != null
        ? [
            {
              xref: "paper",
              yref: "y",
              x: 1.02,
              y: readoutHz,
              xanchor: "left",
              yanchor: "middle",
              text: `<b>${readoutHz.toFixed(3)}</b>`,
              showarrow: false,
              font: { size: 11, color: "#006fff" },
              bgcolor: "rgba(0,111,255,0.08)",
              borderpad: 3,
            },
          ]
        : [],
  };

  const revision = `${chartId}-${windowMs}-${emaTauMs}-${traces.length}-${meterOnlineLabel ?? "na"}-${readoutHz ?? "na"}`;
  return { data: traces, layout, revision, readoutHz };
}

export function buildRegionChartPayload({
  regionId,
  regionLabel,
  meters,
  windowMs,
  emaTauMs,
  nowMs,
  onlineCount,
}: {
  regionId: string;
  regionLabel: string;
  meters: Map<string, RegionalMeterPoint[]>;
  windowMs: number;
  emaTauMs: number | null;
  nowMs: number;
  onlineCount: number;
}): {
  data: Data[];
  layout: Partial<Layout>;
  revision: string;
  readoutHz: number | null;
} {
  const { xMin, xMax } = regionalChartXWindow(meters, windowMs, nowMs);
  const xRange: [Date, Date] = [new Date(xMin), new Date(xMax)];
  const traces: Data[] = [];
  const yValues: number[] = [];

  const meterIds = [...meters.keys()].sort();
  meterIds.forEach((meterId, idx) => {
    const trimmed = trimMeterSamples(meters.get(meterId) ?? [], xMin).filter(
      (p) => p.ts_ms <= xMax,
    );
    if (!trimmed.length) return;
    const color = METER_TRACE_COLORS[idx % METER_TRACE_COLORS.length];
    yValues.push(...trimmed.map((p) => p.freq_hz));
    traces.push({
      name: meterId,
      x: trimmed.map((p) => new Date(p.ts_ms)),
      y: trimmed.map((p) => p.freq_hz),
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", width: 0.5, color },
      opacity: 0.75,
      legendgroup: regionId,
      showlegend: false,
      hovertemplate: `${meterId}<br>%{x|%H:%M:%S.%L}<br>%{y:.4f} Hz<extra></extra>`,
    });
  });

  const trimmedMeters = new Map<string, RegionalMeterPoint[]>();
  for (const [id, samples] of meters) {
    const t = samples.filter((p) => p.ts_ms <= xMax);
    if (t.length) trimmedMeters.set(id, t);
  }

  const avg = buildRegionalAverage(trimmedMeters, emaTauMs, xMin);
  if (avg.x.length) {
    yValues.push(...avg.y);
    traces.push({
      name: "avg",
      x: avg.x,
      y: avg.y,
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", width: 1.5, color: "#006fff" },
      legendgroup: regionId,
      showlegend: false,
      hovertemplate: `avg<br>%{x|%H:%M:%S.%L}<br>%{y:.4f} Hz<extra></extra>`,
    });
  }

  const readoutHz = avg.y.length ? avg.y[avg.y.length - 1]! : null;
  const yRange = yRangeForValues(yValues);
  const meterCountSuffix = formatRegionalMeterCountSuffix(onlineCount);

  const layout: Partial<Layout> = {
    title: {
      text: `<b>${regionId}</b> <span style="font-weight:400;color:#6c757d">${regionLabel}</span>${meterCountSuffix}`,
      font: { size: 11, color: "#1a1c1e" },
      x: 0,
      xanchor: "left",
    },
    paper_bgcolor: "#ffffff",
    plot_bgcolor: "#ffffff",
    margin: { l: 44, r: 80, t: 28, b: 28 },
    font: { family: "Inter, sans-serif", size: 10, color: "#6c757d" },
    xaxis: {
      type: "date",
      range: xRange,
      gridcolor: "#e9ecef",
      linecolor: "#e9ecef",
      tickfont: { size: 9, color: "#6c757d" },
      ...buildDateTickFormatStops(windowMs),
    },
    yaxis: {
      title: { text: "Hz", font: { size: 10, color: "#6c757d" } },
      range: yRange,
      gridcolor: "#e9ecef",
      linecolor: "#e9ecef",
      tickfont: { size: 9, color: "#6c757d" },
      tickformat: ".3f",
    },
    showlegend: false,
    annotations:
      readoutHz != null
        ? [
            {
              xref: "paper",
              yref: "y",
              x: 1.02,
              y: readoutHz,
              xanchor: "left",
              yanchor: "middle",
              text: `<b>${readoutHz.toFixed(3)}</b>`,
              showarrow: false,
              font: { size: 11, color: "#006fff" },
              bgcolor: "rgba(0,111,255,0.08)",
              borderpad: 3,
            },
          ]
        : [],
  };

  const revision = `${regionId}-${windowMs}-${emaTauMs}-${traces.length}-${avg.x.length}-${onlineCount}-${readoutHz ?? "na"}`;

  return { data: traces, layout, revision, readoutHz };
}

function mainDisplaySeries(
  samples: RegionalMeterPoint[],
  xMin: number,
  xMax: number,
  emaTauMs: number | null,
): { x: Date[]; y: number[] } {
  const forEma = samples.filter((p) => p.ts_ms <= xMax);
  if (!forEma.length) return { x: [], y: [] };
  const raw = forEma.map((p) => ({ t: p.ts_ms, v: p.freq_hz }));
  const display =
    emaTauMs != null && emaTauMs > 0
      ? emaSmooth(raw, emaTauMs, xMin)
      : raw.filter((p) => p.t >= xMin);
  return {
    x: display.map((p) => new Date(p.t)),
    y: display.map((p) => p.v),
  };
}

function ribbonXWindow(
  mainSamples: RegionalMeterPoint[],
  metersByRegion: Map<string, Map<string, RegionalMeterPoint[]>>,
  windowMs: number,
  nowMs: number,
): { xMin: number; xMax: number } {
  const cutoff = nowMs - windowMs;
  let earliest = Infinity;
  if (mainSamples.length) earliest = Math.min(earliest, mainSamples[0]!.ts_ms);
  for (const meters of metersByRegion.values()) {
    for (const samples of meters.values()) {
      if (samples.length) earliest = Math.min(earliest, samples[0]!.ts_ms);
    }
  }
  if (!Number.isFinite(earliest)) return { xMin: cutoff, xMax: nowMs };
  if (earliest > cutoff) return { xMin: earliest, xMax: earliest + windowMs };
  return { xMin: cutoff, xMax: nowMs };
}

/** Stacked frequency ribbon — mains on top, then nine NL regions. */
export function buildFreqRibbonChartPayload({
  mainSamples,
  metersByRegion,
  windowMs,
  emaTauMs,
  nowMs,
  shiftMhz,
  rangeMin,
  bare = false,
  lineWidthScale = 1,
}: {
  mainSamples: RegionalMeterPoint[];
  metersByRegion: Map<string, Map<string, RegionalMeterPoint[]>>;
  windowMs: number;
  emaTauMs: number | null;
  nowMs: number;
  /** Spacing between NL regions 2–9 (millihertz); mains→NL_GR is fixed at 20 mHz. */
  shiftMhz: number;
  /** Visible range (minutes) — drives relative-time x-axis ticks. */
  rangeMin: RegionalRangeMinutes;
  /** Transparent paper/plot — map overlay on /frequency. */
  bare?: boolean;
  /** Multiplier for trace line width (e.g. 0.5 on /frequency overlay). */
  lineWidthScale?: number;
}): { data: Data[]; layout: Partial<Layout>; revision: string } {
  const mainsWidth = 2 * lineWidthScale;
  const regionWidth = 1.5 * lineWidthScale;
  const { xMin, xMax } = ribbonXWindow(mainSamples, metersByRegion, windowMs, nowMs);
  const windowSec = windowMs / 1000;
  const relTicks = relativeTimeTicks(regionalRangeMinutesToKey(rangeMin), windowSec);

  type RibbonLine = {
    slot: number;
    id: string;
    x: Date[];
    y: number[];
    color: string;
    width: number;
  };
  const lines: RibbonLine[] = [];

  const main = mainDisplaySeries(mainSamples, xMin, xMax, emaTauMs);
  if (main.x.length) {
    lines.push({
      slot: 0,
      id: "freq/main",
      x: main.x,
      y: main.y,
      color: ECG_MAINS_RIBBON_COLOR,
      width: mainsWidth,
    });
  }

  NL_REGIONS.forEach((region, regionIndex) => {
    const meters = metersByRegion.get(region.id);
    if (!meters) return;
    const trimmedMeters = new Map<string, RegionalMeterPoint[]>();
    for (const [meterId, samples] of meters) {
      const t = samples.filter((p) => p.ts_ms <= xMax);
      if (t.length) trimmedMeters.set(meterId, t);
    }
    const avg = buildRegionalAverage(trimmedMeters, emaTauMs, xMin);
    if (avg.x.length) {
      lines.push({
        slot: regionIndex + 1,
        id: region.id,
        x: avg.x,
        y: avg.y,
        color: ECG_REGION_RIBBON_COLOR,
        width: regionWidth,
      });
    }
  });

  const traces: Data[] = [];
  const yValues: number[] = [];
  const annotations: NonNullable<Layout["annotations"]> = [];

  for (const line of [...lines].sort((a, b) => b.slot - a.slot)) {
    const offset = ribbonLineOffsetHz(line.slot, shiftMhz);
    const yShifted = line.y.map((v) => v + offset);
    yValues.push(...yShifted);
    const xRel = line.x.map((d) => toRelativeSeconds(d.getTime(), xMax));
    const hoverText = line.x.map((d) =>
      d.toLocaleTimeString("en-GB", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
    );
    traces.push({
      name: line.id,
      x: xRel,
      y: yShifted,
      type: "scatter",
      mode: "lines",
      line: { shape: "hv", width: line.width, color: line.color },
      showlegend: false,
      text: hoverText,
      hovertemplate: `${line.id}<br>%{text}<br>%{customdata:.4f} Hz<extra></extra>`,
      customdata: line.y,
    });
    const lastY = yShifted[yShifted.length - 1];
    if (lastY != null && line.x.length) {
      annotations.push({
        xref: "paper",
        yref: "y",
        x: 0,
        xanchor: "right",
        y: lastY,
        yanchor: "middle",
        text: line.id.replace(/_/g, "-"),
        showarrow: false,
        font: { size: 9, color: line.color },
      });
    }
  }

  const yRange = yRangeForValues(yValues);

  const layout: Partial<Layout> = {
    paper_bgcolor: bare ? "transparent" : "#ffffff",
    plot_bgcolor: bare ? "transparent" : "#ffffff",
    margin: bare ? { l: 52, r: 4, t: 4, b: 26 } : { l: 52, r: 4, t: 8, b: 26 },
    font: { family: "Inter, sans-serif", size: 10, color: "#6c757d" },
    xaxis: {
      type: "linear",
      showgrid: false,
      zeroline: false,
      showline: false,
      tickmode: "array",
      tickvals: relTicks.tickvals,
      ticktext: relTicks.ticktext,
      tickfont: { size: 10, color: "#6c757d" },
      autorange: false,
      range: [-windowSec, 0] as [number, number],
    },
    yaxis: {
      range: yRange,
      showticklabels: false,
      showgrid: true,
      gridcolor: "#e9ecef",
      linecolor: "#e9ecef",
      zeroline: false,
    },
    showlegend: false,
    annotations,
  };

  const revision = `ribbon-${windowMs}-${emaTauMs}-${shiftMhz}-${rangeMin}-${lineWidthScale}-${lines.length}-${nowMs}`;
  return { data: traces, layout, revision };
}
